import { get } from "lodash";
import { WatchQueryFetchPolicy } from "@apollo/client";

import { TalentPickData } from "@tvg/ts-types/TalentPicks";
import { Talent } from "@tvg/ts-types/Talent";
import { TalentPickGraphData } from "@tvg/ts-types/Race";
import BetUtils from "@tvg/utils/betSelection";
import { mapTalentPicksListFromGraph } from "@tvg/utils/talentPicksUtils";
import tvgConf from "@tvg/conf";
import { UseTalentPicksProps } from "../types";

const highlightedOptions = {
  product: tvgConf().product.toUpperCase(),
  brand: tvgConf().brand.toUpperCase(),
  device: tvgConf().device
};

const QUERY_VARIABLES = {
  wagerProfile: "PORT-Generic",
  ...highlightedOptions
};

const POLL_INTERVAL = 60000;

const getUniqueTalents = (talents: Array<TalentPickData>): Array<Talent> =>
  talents.reduce(
    (uniqueTalents: Array<Talent>, currentValue) =>
      uniqueTalents.some(
        (elem: Talent) => elem.talentId === currentValue.talent.talentId
      )
        ? uniqueTalents
        : uniqueTalents.concat(currentValue.talent),
    []
  );

export const getTalentPicksWithVisualSelections = (
  talentPicksList: Array<TalentPickGraphData>
) =>
  mapTalentPicksListFromGraph(talentPicksList).map(
    (talentPick: TalentPickData) => {
      const talentPickRace = talentPicksList.find(
        (tp: TalentPickGraphData) => tp.id === talentPick.id
      )?.race;

      // Can't get scratched for legs bet type. To be able to do that we needed the corresponding bi's races info
      const isLegSelections = talentPick.wagerType.legCount > 1;

      return {
        ...talentPick,
        numRunners: talentPickRace?.numRunners,
        visualSelections: BetUtils.getVisualSelections(
          talentPick.selections,
          talentPickRace?.bettingInterests,
          isLegSelections
        )
      };
    }
  );

const getProps = (result: { talentPicksList: Array<TalentPickGraphData> }) => {
  const talentPicksList: Array<TalentPickGraphData> = get(
    result,
    "talentPicksList",
    []
  );
  const talentPicksWithVisualSelections: Array<TalentPickData> =
    getTalentPicksWithVisualSelections(talentPicksList);

  return {
    talentPicks: talentPicksWithVisualSelections,
    talentsList: getUniqueTalents(talentPicksWithVisualSelections)
  };
};

export default {
  // @TODO: validar props
  skip: (props: UseTalentPicksProps) =>
    !get(props, "shouldUpdate") || get(props, "skip"),
  options: (props: UseTalentPicksProps) => {
    const trackCode = get(props, "trackCode", undefined);
    const raceNumber = get(props, "raceNumber", undefined);

    const variables = {
      ...QUERY_VARIABLES,
      wagerProfile: get(props, "wagerProfile", "PORT-Generic"),
      trackCode,
      raceNumber
    };

    return {
      skip: !props.wagerProfile,
      pollInterval: POLL_INTERVAL,
      fetchPolicy: "cache-and-network" as WatchQueryFetchPolicy,
      ssr: false,
      variables
    };
  },
  getProps
};
